import {useQuery} from "@apollo/client";
import React, {useEffect, Fragment} from "react";
import get from "lodash/get";
import Container from "@unibuddy/machop/Experimental/Layout/components/Container/Container";
import ErrorDisplay from "@unibuddy/machop/General/components/ErrorDisplay/ErrorDisplay";
import BuddyCard from "@unibuddy/machop/Buddies/components/BuddyCard/BuddyCard";
import useAnalytics from "@unibuddy/machop/AnalyticsNew/hooks/useAnalytics";
import buddyProfileQuery from "../../queries/buddyProfileQuery";
import {getBuddyPosition, useWindowTitle} from "../../../utils";
import s from "./BuddyProfile.pcss";

const {
    Avatar,
    Name,
    Section,
    Split,
    Left,
    Right,
    Separator,
    Degree,
    Logo,
    CTA,
} = BuddyCard;

function toStr(acc, lang = {}, idx) {
    return `${acc}${idx === 0 ? "" : ", "}${lang.name}`;
}

function getArrayAsString(arr = []) {
    return arr.reduce(toStr, "");
}

const BuddyProfileWrapper = ({mentor, children, buddyPosition}) => {
    const {
        events: {buddyViewed},
    } = useAnalytics();
    useEffect(
        () => {
            buddyViewed({
                id: mentor.id,
                buddyType:
                    mentor.degreeLevel === "Alumni" ? "alumni" : "mentor",
                buddyPosition,
                universitySlug: mentor.university.slug,
            });
        },
        [
            buddyPosition,
            buddyViewed,
            mentor.id,
            mentor.degreeLevel,
            mentor.university,
        ],
    );

    return <React.Fragment>{children}</React.Fragment>;
};

export default function BuddyProfile({mentorId}) {
    const title = "Buddy Profile";
    useWindowTitle(title);
    const buddyPosition = getBuddyPosition();

    const {loading, error, data} = useQuery(buddyProfileQuery, {
        variables: {
            id: mentorId,
        },
        fetchPolicy: "cache-first",
    });

    if (loading) {
        return null;
    }
    const mentor = get(data, "publicMentor", {});

    return (
        <Container>
            <h1 data-test-id="title">{title}</h1>

            <ErrorDisplay error={error}>
                <BuddyProfileWrapper
                    mentor={mentor}
                    buddyPosition={buddyPosition}
                >
                    <BuddyCard
                        elevation={1}
                        className={s.buddyCard}
                    >
                        <Avatar mentor={mentor} />
                        <Name>{mentor.firstName}</Name>
                        <Split>
                            <Left>
                                <Logo mentor={mentor} />
                            </Left>
                            <Right>
                                <Degree mentor={mentor} />
                            </Right>
                        </Split>
                        <CTA
                            className={s.cta}
                            to={`/inbox/chatwith/${
                                mentor.id
                            }?buddyPosition=${buddyPosition}`}
                            mentor={mentor}
                        />
                        <Separator />

                        <Section title="I Come From">
                            {mentor.placeName}
                        </Section>
                        <Section title="I Speak">
                            {getArrayAsString(mentor.languages)}
                        </Section>
                        {mentor.degreeLevel !== "Alumni" ? (
                            <Fragment>
                                <Section title="I'm Currently In">
                                    {mentor.degreeLevel}
                                </Section>
                                <Section title="Favourite Modules">
                                    {getArrayAsString(
                                        mentor.favouriteModules,
                                    )}
                                </Section>
                                <Section title="Previous Studies">
                                    {mentor.pastQualification}
                                </Section>
                                <Section title="Hobbies & Interests">
                                    {getArrayAsString(
                                        mentor.interests,
                                    )}
                                </Section>
                                <Section title="Societies">
                                    {getArrayAsString(
                                        mentor.societies,
                                    )}
                                </Section>
                            </Fragment>
                        ) : null}
                        <Section title="About Me">
                            {mentor.bio}
                        </Section>
                    </BuddyCard>
                </BuddyProfileWrapper>
            </ErrorDisplay>
        </Container>
    );
}
